





















































import Vue from 'vue'
import Analytics from '@/services/Analytics'
import AppSearchBarList from '@/components/AppSearchBarList.vue'
import { mapGetters } from 'vuex'
import { debounce } from 'debounce'
import Person from '@/types/Person.interface'
import personsMixin from '@/mixins/persons-mixin'
import modelsMixin from '@/mixins/models-mixin'
export default Vue.extend({
    mixins: [modelsMixin, personsMixin],
    components: {
        AppSearchBarList
    },
    data() {
        return {
            loading: false
        }
    },
    mounted() {
        //  Show the search icon for users to access the search page workflow when on any page in app besides the search page
        this.$store.commit('searchBarListVisibleSet', true)
    },
    computed: {
        ...mapGetters(['searchBarToggle', 'isApp', 'appDrawerIsMin']),
        hotKeysEnabled(): boolean {
            // Checks if our hotkeys are enabled or not
            return this.$store.getters.settingGet('hotKeysEnabled')
        },
        isSearchPage(): boolean {
            //  Check if we're currently on the persons search page
            const isSearchPage = this.$route.path === '/persons/search'

            //  Return the bool result
            return isSearchPage
        },
        searchBarIndex: {
            get(): number {
                return this.$store.getters.searchBarIndex
            },
            set(value: number): void {
                this.$store.commit('searchBarIndexSet', value)
            }
        },
        searchBarList: {
            get(): Person[] {
                return this.$store.getters.searchBarList
            },
            set(value: Person[]): void {
                this.$store.commit('searchBarListSet', value)
            }
        },
        searchBarListVisible: {
            get(): boolean {
                return this.$store.getters.searchBarListVisible
            },
            set(value: boolean): void {
                this.$store.commit('searchBarListVisibleSet', value)
            }
        },
        searchBarText: {
            get(): string {
                return this.$store.getters.searchBarText
            },
            set(value: string): void {
                this.$store.commit('searchBarTextSet', value)
            }
        }
    },
    methods: {
        //  Focus on the searchbar input
        autoFocus(): void {
            // Only focus on search bar if user has hotkeys enabled
            if (!this.hotKeysEnabled.enabled) {
                return
            }

            //  Fetch the textarea input
            const dom = document.getElementById('searchbox')

            //  Focus on the searchbar input
            dom.focus()
        },
        listHide(): void {
            //  We use a timeout here to make sure the @focusout event is fired last
            setTimeout(() => {
                this.searchBarListVisible = false
            }, 200)
        },
        listShow(): void {
            //  If we are NOT on the persons search page, show the list
            if (!this.isSearchPage) this.searchBarListVisible = true
        },
        goToPerson(person: Person): void {
            this.$router.push(`/inbox?channel=${person.primary_channel_id}`)
            this.reset()
        },
        goToSearchPage() {
            this.$router.push('/persons/search')
        },
        reset(): void {
            this.searchBarText = ''
            this.searchBarIndex = -1
            this.$store.commit('searchBarListSet', null)
        },
        routeKeydownEnter(): void {
            //  Check if the user has selected a search result (ie list line item)
            const hasIndex = this.searchBarIndex > -1

            //  If a search result does NOT exist, we handle creating a new contact
            if (!hasIndex) return this.handleCreate(this.searchBarText, this.goToPerson)

            //  Else, get the search result from the list
            const person = this.searchBarList[this.searchBarIndex]

            //  Navigate to the search result
            this.goToPerson(person)
        },
        moveDown(): void {
            if (this.searchBarIndex + 1 < this.searchBarList.length) {
                this.searchBarIndex = this.searchBarIndex + 1
            }
        },
        moveUp(): void {
            if (this.searchBarIndex !== -1) {
                this.searchBarIndex--
            }
        },
        //  Pops a new contact dialog
        popNewContact(model: Person): void {
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                title: 'New Contact',
                icon: 'mdi-account',
                color: 'primary',
                closeable: true,
                component: 'new-contact-selector',
                props: {
                    model: model,
                    withImport: true
                },
                callback: (person: Person): void => {
                    //  Close the app dialog
                    this.$store.dispatch('dialogClose', 'APP')

                    //  Navigate to the person's messages
                    this.$router.push(`/inbox?channel=${person.primary_channel_id}`)
                }
            })
        },
        async searchDeep(): Promise<void> {
            //  Remove parentheses from our search text because it turns our search query into a regex
            this.searchBarText = this.searchBarText.replace(/[()]/g, '')

            //  Reference the search text (so it is easier to work with)
            const text = this.searchBarText

            //  Validate the search text for length and whitespace
            const isValid = text && text.length > 1 && !text.isNullOrWhiteSpace()

            //  If the search text is NOT valid, do nothing
            if (!isValid) return

            //  Else, hide the search list
            this.searchBarListVisible = false

            //  Assemble search params
            const params = {
                limit: 30,
                searchtext: text,
                paginate: true
            }

            //  Send the search request
            const page = await this.$store.dispatch('personsSearch', params)

            //  If the request failed, do nothing
            if (!page) return

            //  Else, check if we are on the /persons/search page
            const isSearchPage = this.$route.path === '/persons/search'

            //  If we are NOT on the search page, navigate there
            if (!this.isSearchPage) this.$router.push('/persons/search')
        },
        searchQuick: debounce(async function (): Promise<void> {
            //  If we are on the persons search page, go to deep search
            if (this.isSearchPage) return this.searchDeep()

            //  Ref the searchtext (so it is easier to work with)
            const text = this.searchBarText

            //  Validate the search text
            const isValid = text.length > 1 && !text.isNullOrWhiteSpace()

            //  If the search string is NOT valid, do nothing
            if (!isValid) return

            //  Else, assemble search params
            const params = {
                limit: 10,
                filter: 'All',
                searchtext: text,
                paginate: false
            }

            //  Hide the search list
            this.searchBarListVisible = false

            //  Start the loader
            this.loading = true

            //  Send the search request
            const persons = await this.$store.dispatch('personsSearch', params)

            //  Stop the loader
            this.loading = false

            //  If the request failed, do nothing
            if (!persons) return

            //  Update the searchBarList
            this.searchBarList = persons

            //  Show the search list
            this.searchBarListVisible = true
        }, 400),
        // Removes focus from the searchbar enabling user to use hotkeys again
        unFocus(): void {
            // If hotkeys are off, we just return
            if (!this.hotKeysEnabled.enabled) {
                return
            }

            // Get the search html element and then blur it (removes focus)
            const dom = document.getElementById('searchbox')
            dom.blur()
        }
    }
})
